import { useQuery } from '@tanstack/react-query'
import { GitBranch } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../lib/api'
import type { Chat, Topic } from '../lib/apiModels'
import {
  isActive,
  jobTiming,
  SIDE_JOBS_POLL_MS,
  sideJobPath,
  sideJobsPath,
  STATE_LABEL,
  stripSummary,
  type SideJob,
  type SideJobsResponse,
} from '../lib/sideJobs'

const CHIP: Record<SideJob['state'], string> = {
  running: 'bg-teal-100 text-teal-900 dark:bg-teal-900/60 dark:text-teal-100',
  queued: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
  retry_wait: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
  dispatched_unconfirmed: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100',
  failed: 'bg-red-100 text-red-900 dark:bg-red-900/50 dark:text-red-100',
  stopped: 'bg-stone-200 text-stone-800 dark:bg-neutral-800 dark:text-neutral-200',
  done: 'bg-stone-200 text-stone-800 dark:bg-neutral-800 dark:text-neutral-200',
}

/**
 * Side jobs this conversation opened (`pipeline open-topic`), with their real state.
 *
 * Read-only: polls the side-jobs endpoint, whose states come from the side-topic
 * supervisor. Renders nothing when the conversation has no recent side jobs.
 */
export default function SideJobsStrip({
  chatId,
  chat,
  topic,
}: {
  chatId: string
  chat: Chat
  topic: Topic | null
}) {
  const [open, setOpen] = useState(false)
  const topicId = topic?.id ?? null
  const query = useQuery({
    queryKey: ['side_jobs', chatId, topicId],
    queryFn: () => api<SideJobsResponse>(sideJobsPath(chatId, topicId)),
    enabled: !chat.archived,
    refetchInterval: SIDE_JOBS_POLL_MS,
    staleTime: 15_000,
  })
  const jobs = query.data?.jobs ?? []
  if (jobs.length === 0) return null
  const nowSeconds = Date.now() / 1000
  const anyActive = jobs.some(isActive)

  return (
    <div className="px-3 pt-2" data-testid="side-jobs-strip">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={`w-full min-h-11 flex items-center justify-between gap-2 px-4 py-2 rounded-xl text-sm border ${
          anyActive
            ? 'border-teal-300 bg-teal-50 text-teal-950 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-100'
            : 'border-stone-300 bg-stone-50 text-stone-800 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200'
        }`}
      >
        <span className="flex items-center gap-2 font-medium">
          <GitBranch className="h-4 w-4 shrink-0" aria-hidden="true" />
          Side jobs
        </span>
        <span className="text-xs text-right">{stripSummary(jobs)} {open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <ul className="mt-1 rounded-xl text-sm border border-stone-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 divide-y divide-stone-100 dark:divide-neutral-800 max-h-72 overflow-y-auto">
          {jobs.map((job) => (
            <li key={job.topic_id}>
              <Link
                to={sideJobPath(job)}
                className="flex min-h-11 items-start justify-between gap-3 px-4 py-2 text-stone-800 dark:text-neutral-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-600"
              >
                <span className="min-w-0">
                  <span className="block break-words">{job.title}</span>
                  <span className="block text-xs text-stone-500 dark:text-neutral-400">{jobTiming(job, nowSeconds)}</span>
                </span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${CHIP[job.state]}`}>
                  {STATE_LABEL[job.state]}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
